import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { ArrowRight, Brain, Heart, Sparkles, Users, CheckCircle, Play, MessageCircle, BarChart3 } from 'lucide-react';
import { useSchema } from '../hooks/useSchema';
import { useAuth } from '../contexts/AuthContext';
import PublicHeader from '../components/PublicHeader';

const HowItWorks = () => {
  const { isAuthenticated } = useAuth();
  
  const steps = [
    {
      number: "01",
      title: "Check In With Yourself",
      description: "Start by noticing how you're feeling in your body and emotions. Our gentle prompts help you identify what's happening without judgment.",
      icon: Heart,
      color: "bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400"
    },
    {
      number: "02",
      title: "Get Personalized Guidance",
      description: "Based on your check-in, we suggest the right tools for your current state — whether you need to calm down, energize, or find clarity.",
      icon: Sparkles,
      color: "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400"
    },
    {
      number: "03",
      title: "Use Regulation Tools",
      description: "Access breathing exercises, guided meditations, body-based techniques, and cognitive reframes designed specifically for ADHD minds.",
      icon: Brain,
      color: "bg-primary/10 text-primary"
    },
    {
      number: "04",
      title: "Track Your Progress",
      description: "See patterns in your emotional regulation over time. Celebrate wins and identify triggers to build lasting self-awareness.",
      icon: BarChart3,
      color: "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400"
    }
  ];

  const features = [
    {
      icon: MessageCircle,
      title: "AI Companion",
      description: "A supportive guide that understands ADHD. Ask questions, get encouragement, or process difficult emotions anytime."
    },
    {
      icon: Play,
      title: "Audio Guidance",
      description: "Professional voice-guided exercises so you can close your eyes and just follow along — no reading required."
    },
    {
      icon: Users,
      title: "Relationship Tools",
      description: "Specialized features for ADHD-affected relationships, including partner check-ins and communication aids."
    }
  ];

  // SEO Schema - HowTo structured data
  useSchema({
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to Use CoRegulate for ADHD Emotional Regulation",
    "description": "A step-by-step guide to regulating emotions with CoRegulate",
    "image": "https://coregulateadhd.com/og-image.png",
    "step": [
      {
        "@type": "HowToStep",
        "position": 1,
        "name": "Check In With Yourself",
        "text": "Start by noticing how you're feeling in your body and emotions. Our gentle prompts help you identify what's happening without judgment."
      },
      {
        "@type": "HowToStep",
        "position": 2,
        "name": "Get Personalized Guidance",
        "text": "Based on your check-in, we suggest the right tools for your current state — whether you need to calm down, energize, or find clarity."
      },
      {
        "@type": "HowToStep",
        "position": 3,
        "name": "Use Regulation Tools",
        "text": "Access breathing exercises, guided meditations, body-based techniques, and cognitive reframes designed specifically for ADHD minds."
      },
      {
        "@type": "HowToStep",
        "position": 4,
        "name": "Track Your Progress",
        "text": "See patterns in your emotional regulation over time. Celebrate wins and identify triggers to build lasting self-awareness."
      }
    ]
  });

  return (
    <>
      <Helmet>
        <title>How It Works | CoRegulate - ADHD Emotional Regulation App</title>
        <meta name="description" content="Discover how CoRegulate helps you regulate emotions with ADHD. Step-by-step guidance, personalized tools, and AI support designed for neurodivergent minds." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://coregulateadhd.com/how-it-works" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="How CoRegulate Works | ADHD Emotional Regulation" />
        <meta property="og:description" content="Discover how CoRegulate helps you regulate emotions with ADHD. Step-by-step guidance designed for neurodivergent minds." />
        <meta property="og:url" content="https://coregulateadhd.com/how-it-works" />
        <meta property="og:site_name" content="CoRegulate" />
        <meta property="og:image" content="https://coregulateadhd.com/og-image.png" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="How CoRegulate Works | ADHD Emotional Regulation" />
        <meta name="twitter:description" content="Discover how CoRegulate helps you regulate emotions with ADHD. Step-by-step guidance designed for neurodivergent minds." />
        <meta name="twitter:image" content="https://coregulateadhd.com/og-image.png" />
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-b from-[#FEF6EF] to-[#FDF8F4]">
        {/* Header - Matching site style */}
        <PublicHeader currentPage="how-it-works" />

        <main className="pt-20">
          {/* Hero Section */}
          <section className="py-16 md:py-24 px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-serif text-[#1D3D3D] mb-6">
                How <span className="text-[#1D6B6B]">CoRegulate</span> Works
              </h1>
              <p className="text-lg md:text-xl text-[#5A7A7A] max-w-2xl mx-auto">
                A gentle, step-by-step approach to emotional regulation — designed specifically 
                for how ADHD minds work. No overwhelm, just soft guidance.
              </p>
            </div>
          </section>

          {/* Steps Section */}
          <section className="py-12 px-4 bg-white/50">
            <div className="max-w-5xl mx-auto">
              <div className="grid gap-8">
                {steps.map((step, index) => (
                  <div 
                    key={step.number}
                    className="flex flex-col md:flex-row items-start gap-6 p-6 md:p-8 bg-white rounded-2xl border border-[#E8E1D6] shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="flex-shrink-0">
                      <div className={`w-16 h-16 rounded-2xl flex items-center justify-center ${step.color}`}>
                        <step.icon className="w-8 h-8" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-sm font-mono text-[#7D8E95]">{step.number}</span>
                        <h2 className="text-xl md:text-2xl font-serif text-[#1D3D3D]">{step.title}</h2>
                      </div>
                      <p className="text-[#5A7A7A] leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                    {index < steps.length - 1 && (
                      <div className="hidden md:block flex-shrink-0 self-center">
                        <ArrowRight className="w-5 h-5 text-[#1D6B6B]/30" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Key Features */}
          <section className="py-16 px-4">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-serif text-[#1D3D3D] text-center mb-12">
                Built for ADHD Minds
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                {features.map((feature) => (
                  <div 
                    key={feature.title}
                    className="bg-white rounded-2xl p-6 border border-[#E8E1D6] text-center shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="w-12 h-12 bg-[#1D6B6B]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                      <feature.icon className="w-6 h-6 text-[#1D6B6B]" />
                    </div>
                    <h3 className="text-lg font-semibold text-[#1D3D3D] mb-2">{feature.title}</h3>
                    <p className="text-sm text-[#5A7A7A]">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Why It Works */}
          <section className="py-16 px-4 bg-white/50">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-serif text-[#1D3D3D] text-center mb-8">
                Why CoRegulate Works for ADHD
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  "Short, manageable exercises (1-5 minutes)",
                  "Visual and audio options — choose what works",
                  "No complicated navigation or setup",
                  "Meets you where you are emotionally",
                  "Created by a Clinical Hypnotherapist",
                  "Progress without pressure",
                  "Body-based techniques that bypass overthinking",
                  "AI companion available 24/7"
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 bg-white rounded-xl border border-[#E8E1D6] shadow-sm">
                    <CheckCircle className="w-5 h-5 text-[#1D6B6B] flex-shrink-0" />
                    <span className="text-[#1D3D3D]">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-16 md:py-24 px-4">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="text-2xl md:text-3xl font-serif text-[#1D3D3D] mb-4">
                Ready to Start Regulating?
              </h2>
              <p className="text-[#5A7A7A] mb-8">
                Join thousands of people with ADHD who are building emotional resilience, 
                one soft action at a time.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/auth" 
                  className="inline-flex items-center justify-center gap-2 text-white px-8 py-3 rounded-full font-medium hover:opacity-90 transition-all shadow-md"
                  style={{
                      background: 'linear-gradient(135deg, #7D8E95 0%, #2A8585 35%, #1D6B6B 65%, #FFBB98 100%)'
                  }}
                >
                  Start for Free
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link 
                  to="/pricing" 
                  className="inline-flex items-center justify-center gap-2 bg-white text-[#1D3D3D] px-8 py-3 rounded-full font-medium border border-[#E8E1D6] hover:bg-white/70 transition-colors"
                >
                  View Pricing
                </Link>
              </div>
            </div>
          </section>
        </main>

        {/* Footer */}
        <footer className="bg-white/80 border-t border-[#E8E1D6]">
          <div className="max-w-6xl mx-auto px-4 py-8 text-center space-x-6">
            <Link to="/about" className="text-[#7D8E95] hover:text-[#1D6B6B] transition-colors">
              About
            </Link>
            <Link to="/tools" className="text-[#7D8E95] hover:text-[#1D6B6B] transition-colors">
              Tools
            </Link>
            <Link to="/pricing" className="text-[#7D8E95] hover:text-[#1D6B6B] transition-colors">
              Pricing
            </Link>
            <Link to="/contact" className="text-[#7D8E95] hover:text-[#1D6B6B] transition-colors">
              Contact
            </Link>
          </div>
        </footer>
      </div>
    </>
  );
};

export default HowItWorks;
